import { useState } from "react";

import { Alert } from "@/components/alert/alert";
import { useDuelQueue } from "@/hooks/use-duel-queue";
import { useRaidStatus } from "@/hooks/use-raid-status";
import { useAuth } from "@/providers/auth-provider";
import { useDuel } from "@/providers/duel-provider";
import { useRaid } from "@/providers/raid-provider";
import { useSPAContext } from "@/providers/spa-provider";
import { RouteNames } from "@/types/route-names";

import { ArenaCard } from "./arena-card";
import { CampaignCard } from "./campaign-card";
import { RaidsCard } from "./raids-card";
import { TrainingPvpModeCard } from "./training-pvp-mode-card";

/**
 * Play Menu View - Entry point for Arena (Duels) and Raids.
 */
export function PlayMenuView() {
  const { setCurrentRoute } = useSPAContext();
  const { getAccessToken, state: authState } = useAuth();
  const { connectToDuel } = useDuel();
  const { raidState, setRaidState } = useRaid();

  const { queueStatus, currentDuelId } = useDuelQueue(true);
  const { currentRaidId } = useRaidStatus(true);

  const [isInitializingDuel, setIsInitializingDuel] = useState(false);

  const handleStartDuel = async () => {
    if (isInitializingDuel) return;

    const accessToken = getAccessToken();
    if (!accessToken || authState.status !== "authenticated") {
      Alert("Authentication error. Please try again");
      return;
    }

    const playerId = authState.user?.telegram_id;
    if (!playerId) {
      Alert("Authentication error. Please try again");
      return;
    }

    // Navigate to team selection instead of directly enqueueing
    setCurrentRoute(RouteNames.ARENA_TEAM_SELECT);
  };

  const handleReconnectToDuel = async () => {
    if (isInitializingDuel || !currentDuelId) return;

    const accessToken = getAccessToken();
    if (!accessToken || authState.status !== "authenticated") {
      Alert("Authentication error. Please try again");
      return;
    }

    const playerId = authState.user?.telegram_id;
    if (!playerId) {
      Alert("Authentication error. Please try again");
      return;
    }

    setIsInitializingDuel(true);
    try {
      setCurrentRoute(RouteNames.DUEL_QUEUE);
      const wsUrl = import.meta.env.VITE_WS_API_URL;
      if (!wsUrl) {
        Alert("Internal server error. Please contact support");
        return;
      }
      await connectToDuel(currentDuelId, playerId, accessToken, wsUrl, true);
    } catch (error) {
      console.error("Failed to reconnect to duel:", error);
      Alert("Failed to reconnect to duel");
    } finally {
      setIsInitializingDuel(false);
    }
  };

  const handleReconnectToRaid = () => {
    if (!currentRaidId) return;

    // If already connected to this raid and not in connecting state, just navigate
    if (
      raidState.raid_id === currentRaidId &&
      raidState.status !== "idle" &&
      raidState.status !== "connecting"
    ) {
      setCurrentRoute(RouteNames.RAID_ACTIVE);
      return;
    }

    // Otherwise, set connecting state to trigger connection
    setRaidState((prev) => ({
      ...prev,
      raid_id: currentRaidId,
      status: "connecting",
    }));
    setCurrentRoute(RouteNames.RAID_ACTIVE);
  };

  const handleStartRaid = () => {
    setCurrentRoute(RouteNames.RAID_START);
  };

  const isDuelActive = queueStatus === "IN_DUEL" || queueStatus === "MATCHED";
  const isDuelWaiting = queueStatus === "WAITING";

  return (
    <div className="relative flex flex-col bg-pk-night-1/90 backdrop-blur-md pb-24 w-full h-full overflow-y-auto">
      <div className="z-10 relative flex justify-between items-center mt-6 xs:mt-8">
        <div className="flex flex-col ml-4 p-2">
          <div className="relative">
            <h1 className="drop-shadow-lg font-black text-white text-3xl xs:text-4xl uppercase leading-none tracking-tight">
              PLAY
            </h1>
            <div className="bg-pk-yellow-1 mt-1 w-12 xs:w-16 h-1 skew-x-[-20deg]" />
          </div>
        </div>
      </div>

      <div className="z-10 relative flex flex-col items-center gap-4 xs:gap-6 mx-auto px-4 pt-4 xs:pt-6 w-full max-w-md">
        <ArenaCard isDuelActive={isDuelActive} isDuelWaiting={isDuelWaiting} isInitializingDuel={isInitializingDuel} handleReconnectToDuel={handleReconnectToDuel} handleStartDuel={handleStartDuel} />
        <TrainingPvpModeCard />
        <RaidsCard currentRaidId={currentRaidId} handleReconnectToRaid={handleReconnectToRaid} handleStartRaid={handleStartRaid} isInitializingDuel={isInitializingDuel} />
        <CampaignCard />
      </div>
    </div>
  );
}
